import {
  faEllipsisVertical,
  faSort,
  faSortDown,
  faSortUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { useCallback, useEffect, useState, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import BillingReportRepository from '@/repositories/BillingReportRepository';
import { DIRECTION } from '@/types/sort';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import Dropdown from '@/ui/components/Dropdown';
import Paginator from '@/ui/components/Paginator';
import ajax from '@/utils/ajax';

// Types (minimal subset to render table)
type BillingReport = {
  id: string;
  name: string;
  row_count?: number;
  submitted_count?: number;
  range_start?: string | null;
  range_end?: string | null;
  most_recent_result_completed_at?: string | null;
  filter_description?: string | null;
  state?: string | null;
  latest_result_id: number | null;
  created_at?: string | null;
};

function billingReportBadgeColor(state: string | null | undefined): Color {
  switch (state) {
    case 'pending':
      return Color.GRAY;
    case 'processing':
      return Color.BLUE;
    case 'errors':
      return Color.GREEN;
    case 'failure':
      return Color.RED;
    case 'submitted':
      return Color.INDIGO;

    default:
      return Color.GRAY;
  }
}

const billingReportsDefaults = {
  direction: DIRECTION.DESC,
  ordering: 'created_at'
};

const BillingReports = (): JSX.Element => {
  const { host } = useContext(AppContext);
  const navigate = useNavigate();
  const repository = BillingReportRepository();
  const [reports, setReports] = useState<BillingReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const ordering = searchParams.get('ordering') ?? billingReportsDefaults.ordering;
  const direction = searchParams.get('direction') ?? billingReportsDefaults.direction;
  const orderingParam = `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`;
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const setPage = (nextPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage > 1) next.set('page', String(nextPage));
      else next.delete('page');
      return next;
    });
  };

  const toggleSort = (field: string) => {
    const nextDirection =
      ordering === field && direction === DIRECTION.DESC ? DIRECTION.ASC : DIRECTION.DESC;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('ordering', field);
      next.set('direction', nextDirection);
      next.delete('page');
      return next;
    });
  };

  const sortIcon = (field: string) => {
    if (ordering !== field) return faSort;
    return direction === DIRECTION.ASC ? faSortUp : faSortDown;
  };

  const sortableHeader = (field: string, label: string) => (
    <th>
      <button type="button" onClick={() => toggleSort(field)} className="flex items-center gap-1">
        {label}
        <FontAwesomeIcon icon={sortIcon(field)} className="text-gray-400" />
      </button>
    </th>
  );

  const loadReports = useCallback(
    async (pageNum = page) => {
      setLoading(true);
      try {
        const res = await ajax.get({
          url: `${host}/api/billing/billing_reports/?page=${pageNum}&ordering=${orderingParam}`
        });
        if (res.status === 200) {
          setReports(res.data.results || res.data || []);
          setTotalPages(res.data.total_pages || 1);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [host, page, orderingParam]
  );

  useEffect(() => {
    loadReports(page);
  }, [loadReports, page]);

  const handleDelete = async (report: BillingReport) => {
    if (!window.confirm(`Delete "${report.name}"? This cannot be undone.`)) return;
    setDeletingId(report.id);
    const result = await repository.deleteBillingReport(String(report.id));
    setDeletingId(null);
    if (result.isError) {
      alert(result.errorMessage);
    } else {
      await loadReports(page);
    }
  };

  return (
    <Container type="full-width-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Billing Reports</h1>
        <div>
          <Button color="blue" onClick={() => navigate('/billing/reports/new')}>
            New Billing Report
          </Button>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : reports.length === 0 ? (
        <Card className="success p-4">No Billing Reports found.</Card>
      ) : (
        <Card className="flex flex-col">
          <table className="table-auto w-full border-collapse text-left">
            <thead>
              <tr>
                <th>Name</th>
                {sortableHeader('created_at', 'Created')}
                {sortableHeader('range_start', 'Range Start')}
                {sortableHeader('range_end', 'Range End')}
                <th>Last Updated</th>
                <th>Filters</th>
                <th>State</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id}>
                  <td className="font-medium ">
                    <Link
                      to={`/billing/reports/${report.id}/results/${report.latest_result_id}/`}
                      className="flex items-center text-indigo-600 hover:text-indigo-900">
                      {report.name}
                    </Link>
                  </td>
                  <td>
                    {report.created_at ? new Date(report.created_at).toLocaleDateString() : ''}
                  </td>
                  <td>
                    {report.range_start ? new Date(report.range_start).toLocaleDateString() : ''}
                  </td>
                  <td>{report.range_end ? new Date(report.range_end).toLocaleDateString() : ''}</td>
                  <td>
                    {report.most_recent_result_completed_at
                      ? new Date(report.most_recent_result_completed_at).toLocaleDateString()
                      : ''}
                  </td>
                  <td>{report.filter_description ?? ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge
                      text={report.state ?? 'N/A'}
                      color={billingReportBadgeColor(report.state)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        color="white"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/billing/reports/${report.id}/results/${report.latest_result_id}/`
                          )
                        }>
                        Open
                      </Button>
                      <Dropdown
                        buttonText={<FontAwesomeIcon icon={faEllipsisVertical} />}
                        showChevronIcon={false}
                        customButtonClassName="p-1 text-gray-400 hover:text-gray-600"
                        items={[
                          <Link
                            key="edit"
                            to={`/billing/reports/${report.id}/edit`}
                            className="block px-3 py-2 text-sm text-gray-700 w-full">
                            Edit
                          </Link>,
                          <button
                            key="delete"
                            onClick={() => handleDelete(report)}
                            disabled={deletingId === report.id}
                            className="block px-3 py-2 text-sm text-red-600 w-full text-left disabled:opacity-50">
                            {deletingId === report.id ? 'Deleting…' : 'Delete'}
                          </button>
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4">
            <Paginator
              currentPage={page}
              totalPages={totalPages}
              goToPage={setPage}
              nextPage={() => setPage(Math.min(totalPages, page + 1))}
              prevPage={() => setPage(Math.max(1, page - 1))}
            />
          </div>
        </Card>
      )}
    </Container>
  );
};

export default BillingReports;
